/**
 * Bloco IPCA da aba Juros & Inflação (#220):
 *  (a) mini-gráfico de barras do REALIZADO (SGS 433, últimos 24 meses, último
 *      mês destacado);
 *  (b) cards da implícita 1y/3y/5y (vencimento com du mais próximo de
 *      252/756/1260);
 *  (c) linha discreta do Focus com rótulo "cross-check" — NÃO é a fonte do
 *      projetado (textos `fonte_*` do payload renderizados verbatim).
 */
import { useMemo } from "react";
import {
  Bar,
  BarChart,
  Cell,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { IpcaResponse } from "../../../api/types";

const MESES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

function labelMes(iso: string): string {
  const m = parseInt(iso.slice(5, 7), 10) - 1;
  return `${MESES[m] ?? iso.slice(5, 7)}/${iso.slice(2, 4)}`;
}

function fmt(n: number, dec = 2): string {
  return n.toLocaleString("pt-BR", { minimumFractionDigits: dec, maximumFractionDigits: dec });
}

const HORIZONTES: Array<{ rotulo: string; duAlvo: number }> = [
  { rotulo: "1 ano", duAlvo: 252 },
  { rotulo: "3 anos", duAlvo: 756 },
  { rotulo: "5 anos", duAlvo: 1260 },
];

export function IpcaBlock({ ipca }: { ipca: IpcaResponse }) {
  const realizado24m = useMemo(
    () =>
      ipca.realizado.slice(-24).map((r) => ({
        ...r,
        label: labelMes(r.mes_ref),
        ultimo: r.mes_ref === ipca.ultimo_realizado,
      })),
    [ipca],
  );

  const cards = useMemo(
    () =>
      HORIZONTES.map((h) => {
        if (ipca.projetado.length === 0) return { ...h, row: null };
        const row = ipca.projetado.reduce((best, r) =>
          Math.abs(r.du - h.duAlvo) < Math.abs(best.du - h.duAlvo) ? r : best,
        );
        return { ...h, row };
      }),
    [ipca],
  );

  return (
    <div className="rounded-xl border border-white/10 bg-indra-azul-surface p-4" data-testid="ipca-block">
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
        <h3 className="text-white font-semibold text-sm">IPCA — realizado × implícita de mercado</h3>
        <span className="text-white/40 text-[11px]">
          realizado: {ipca.fonte_realizado}
          {ipca.ultimo_realizado ? ` · último ${labelMes(ipca.ultimo_realizado)}` : ""}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* (a) realizado 24m */}
        <div>
          <div className="text-[10px] uppercase tracking-wider text-white/50 mb-1">
            Variação mensal realizada (%)
          </div>
          {realizado24m.length === 0 ? (
            <div className="text-white/40 text-xs py-10 text-center" data-testid="ipca-realizado-vazio">
              Sem IPCA realizado materializado ainda.
            </div>
          ) : (
            <div className="h-40" data-testid="ipca-realizado-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={realizado24m} margin={{ top: 4, right: 8, left: -16, bottom: 0 }}>
                  <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="3 3" />
                  <XAxis
                    dataKey="label"
                    stroke="rgba(255,255,255,0.5)"
                    fontSize={10}
                    tickLine={false}
                    minTickGap={24}
                    interval="preserveStartEnd"
                  />
                  <YAxis stroke="rgba(255,255,255,0.5)" fontSize={10} tickLine={false} />
                  <Tooltip
                    formatter={(v: number) => [`${fmt(v)}%`, "IPCA mês"]}
                    labelStyle={{ color: "rgba(255,255,255,0.8)" }}
                    // #228 item 1: sem itemStyle o recharts usa a cor da série
                    // (cinza 25% nas barras) — valor ilegível no fundo escuro.
                    itemStyle={{ color: "#fff" }}
                    contentStyle={{
                      backgroundColor: "#0c2340",
                      border: "1px solid rgba(255,255,255,0.15)",
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                  />
                  <Bar dataKey="variacao_pct" isAnimationActive={false}>
                    {realizado24m.map((r) => (
                      <Cell
                        key={r.mes_ref}
                        fill={r.ultimo ? "#00DAC8" : "rgba(255,255,255,0.25)"}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* (b) implícita por horizonte */}
        <div>
          <div className="text-[10px] uppercase tracking-wider text-white/50 mb-1">
            Inflação implícita (% a.a.) — as-of {ipca.data_ref_implicita ?? "—"}
          </div>
          <div className="grid grid-cols-3 gap-2">
            {cards.map((c) => (
              <div
                key={c.rotulo}
                className="rounded-lg bg-white/5 border border-white/10 px-3 py-2"
                data-testid={`ipca-card-${c.duAlvo}`}
              >
                <div className="text-[11px] uppercase tracking-wide text-amber-300/90">{c.rotulo}</div>
                <div className="text-white font-mono font-semibold text-lg">
                  {c.row ? `${fmt(c.row.implicita_aa)}%` : "—"}
                </div>
                {c.row && (
                  <div className="text-white/40 text-[10px]">
                    cupom {fmt(c.row.cupom_ipca_aa)}% · {c.row.du} du
                  </div>
                )}
              </div>
            ))}
          </div>
          <p className="text-white/40 text-[11px] mt-2" data-testid="ipca-fonte-projetado">
            projetado: {ipca.fonte_projetado}
          </p>

          {/* (c) Focus cross-check — discreto, claramente NÃO-fonte */}
          {ipca.focus.length > 0 && (
            <div
              className="mt-3 rounded-lg border border-dashed border-white/15 px-3 py-2"
              data-testid="ipca-focus-linha"
            >
              <span className="text-[10px] uppercase tracking-wider text-white/40 mr-2">
                Focus (cross-check{ipca.data_ref_focus ? ` · ${ipca.data_ref_focus}` : ""})
              </span>
              <span className="text-white/60 text-xs font-mono">
                {ipca.focus.map((f) => `${f.ano}: ${fmt(f.mediana_pct)}%`).join(" · ")}
              </span>
              <p className="text-white/35 text-[10px] mt-1">{ipca.fonte_focus}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
